import { useEffect, useRef, type ReactNode, type HTMLAttributes } from "react";

/* ─── Reveal on scroll (fade-up) ─────────────────────── */
/** 스크롤 진입 시 fade-up 애니메이션. IntersectionObserver 기반, prefers-reduced-motion 자동 회피. */
export function Reveal({
  children, as: As = "div", delay = 0, className, ...rest
}: { children: ReactNode; as?: any; delay?: number; className?: string } & HTMLAttributes<HTMLElement>) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) {
      el.classList.add("is-revealed");
      return;
    }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          setTimeout(() => e.target.classList.add("is-revealed"), delay);
          io.unobserve(e.target);
        }
      }),
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [delay]);
  return <As ref={ref} className={`reveal ${className ?? ""}`} {...rest}>{children}</As>;
}

/* ─── 3D Tilt card ──────────────────────────────────── */
/** 마우스 위치에 따라 카드가 살짝 기울어짐 (perspective). */
export function Tilt({ children, className, max = 8, ...rest }: { children: ReactNode; className?: string; max?: number } & HTMLAttributes<HTMLDivElement>) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let raf = 0;
    const onMove = (e: MouseEvent) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.style.transform = `perspective(800px) rotateY(${x * max}deg) rotateX(${-y * max}deg)`;
        el.style.setProperty("--spot-x", `${(x + 0.5) * 100}%`);
        el.style.setProperty("--spot-y", `${(y + 0.5) * 100}%`);
      });
    };
    const onLeave = () => {
      cancelAnimationFrame(raf);
      el.style.transform = "";
      el.style.setProperty("--spot-x", "50%");
      el.style.setProperty("--spot-y", "50%");
    };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
      cancelAnimationFrame(raf);
    };
  }, [max]);
  return <div ref={ref} className={`tilt ${className ?? ""}`} {...rest}>{children}</div>;
}

/* ─── Magnetic button ───────────────────────────────── */
/** 마우스가 근처에 오면 버튼이 따라옴. */
export function Magnetic({ children, className, strength = 0.25, ...rest }: { children: ReactNode; className?: string; strength?: number } & HTMLAttributes<HTMLButtonElement>) {
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let raf = 0;
    const onMove = (e: MouseEvent) => {
      const r = el.getBoundingClientRect();
      const x = e.clientX - r.left - r.width / 2;
      const y = e.clientY - r.top - r.height / 2;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.style.transform = `translate(${x * strength}px, ${y * strength}px)`;
      });
    };
    const onLeave = () => { cancelAnimationFrame(raf); el.style.transform = ""; };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
      cancelAnimationFrame(raf);
    };
  }, [strength]);
  return <button ref={ref} className={`magnetic ${className ?? ""}`} {...rest}>{children}</button>;
}

/* ─── Ripple click effect (attach to any clickable) ─── */
export function onRippleClick(e: React.MouseEvent<HTMLElement>) {
  const el = e.currentTarget;
  const r = el.getBoundingClientRect();
  const size = Math.max(r.width, r.height);
  const ripple = document.createElement("span");
  ripple.className = "ripple";
  ripple.style.width = ripple.style.height = `${size}px`;
  ripple.style.left = `${e.clientX - r.left - size / 2}px`;
  ripple.style.top = `${e.clientY - r.top - size / 2}px`;
  el.appendChild(ripple);
  ripple.addEventListener("animationend", () => ripple.remove(), { once: true });
}
